/**
 * FloatingContactWidget
 * ─────────────────────
 * A slim floating action button in the bottom-right showing "WhatsApp" +
 * "Call" quick-access, aimed at recovering leads that would otherwise
 * bounce off the form. Digital-marketing-team recommendation, Feb 2026.
 *
 * Rendered on: landing pages (/gaming, /fashion, /interior-design, etc.),
 *              /counseling, /contact, /claim-profile.
 *              (NOT on /, /explore, /library — those get in the way of
 *               browsing rather than helping conversion.)
 *
 * UX decisions:
 *   - Default state: single collapsed pink pill "Chat with us" — feels less
 *     intrusive than two open buttons stacked (mobile screens are precious).
 *   - Tap → expands to reveal WhatsApp + Call. Second tap on either →
 *     fires the wa.me / tel: link + analytics conversion event.
 *   - Message is pre-filled with the course/context we can infer from the
 *     current route so Saleem doesn't have to ask "which course?" twice.
 *   - Availability strip: green pulse "Team available" on IST business hours
 *     (10 AM – 8 PM, Mon-Sat), otherwise "Back within 24h" — sets honest
 *     expectations, still allows async WhatsApp.
 */
import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, Phone, X } from 'lucide-react';
import { trackConversion, trackEvent } from '../lib/analytics';

// Single canonical contact for now. Keeping them here rather than in .env
// because the wa.me + tel: links are 100% client-side and going through
// process.env would bake them into the JS bundle anyway.
const CONTACT_PHONE_E164 = '+918019003547';
const CONTACT_WHATSAPP_E164 = '918019003547';   // wa.me needs no `+`

function inferContext(pathname) {
    // Landing pages: /careers/:slug or /gaming, /fashion, etc.
    // NOTE: keep every slug used in App.jsx <Route path=...> here. Testing
    // agent iteration_29 caught 4 missing entries which meant those pages
    // fell through to the generic "learning more" message — a real
    // lead-intent leak because the marketing team runs course-specific ads.
    const parts = (pathname || '').split('/').filter(Boolean);
    const slugToLabel = {
        'gaming':          'Gaming careers',
        'fashion':         'Fashion design',
        'interior-design': 'Interior design',
        'graphic-design':  'Graphic design',
        'ux-ui-design':    'UX/UI design',       // route in App.jsx is /ux-ui-design
        'animation':       'Animation careers',
        'jewellery-design':'Jewellery design',
        'filmmaking':      'Film making',
        'photography':     'Photography',
        'film-making':     'Film making',        // legacy alias, safe to keep
        'ux-ui':           'UX/UI design',       // legacy alias, safe to keep
        'counseling':      'a free counseling call',
        'admission':       'admissions info',
        'contact':         'Eager (from your contact form)',
        'claim-profile':   'a partnership discussion',
    };
    for (const p of parts) {
        if (slugToLabel[p]) return slugToLabel[p];
    }
    if (parts[0] === 'careers' && parts[1] && slugToLabel[parts[1]]) return slugToLabel[parts[1]];
    return null;
}

function buildWaLink(context) {
    const line = context
        ? `Hi Eager, I'm interested in ${context}. Please share more info.`
        : `Hi Eager, I'm interested in learning more about your programs.`;
    return `https://wa.me/${CONTACT_WHATSAPP_E164}?text=${encodeURIComponent(line)}`;
}

// India business hours (IST). Mon-Sat, 10 AM – 8 PM.
function isTeamAvailable(now = new Date()) {
    // Convert to IST (UTC+05:30) via Intl API. Cheaper than a Date library.
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Kolkata',
        weekday: 'short', hour: '2-digit', hour12: false,
    }).formatToParts(now);
    const wk = parts.find(p => p.type === 'weekday')?.value || 'Sun';
    const hr = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
    if (wk === 'Sun') return false;
    return hr >= 10 && hr < 20;
}

export default function FloatingContactWidget({ enabled = true }) {
    // Dismissed = user clicked the small × to hide entirely for this session.
    // Otherwise WhatsApp + Call are shown directly (no intermediate "Chat with us" tap).
    const [dismissed, setDismissed] = useState(false);
    const location = useLocation();
    const context = useMemo(() => inferContext(location.pathname), [location.pathname]);
    const available = isTeamAvailable();

    if (!enabled || dismissed) return null;

    const waLink = buildWaLink(context);
    const telLink = `tel:${CONTACT_PHONE_E164}`;

    const onWaClick = () => {
        try {
            trackEvent('lead_capture', 'floating_whatsapp_click', context || 'default');
            trackConversion('Contact', { method: 'whatsapp_floating', content_category: context || 'general' });
        } catch { /* analytics best-effort */ }
    };
    const onCallClick = () => {
        try {
            trackEvent('lead_capture', 'floating_call_click', context || 'default');
            trackConversion('Contact', { method: 'call_floating', content_category: context || 'general' });
        } catch { /* analytics best-effort */ }
    };

    return (
        <div
            data-testid="floating-contact-widget"
            className="fixed bottom-4 right-4 z-40 flex flex-col items-end gap-2 pointer-events-none"
        >
            {/* Direct action buttons — visible on load, no toggle click required. */}
            <div className="pointer-events-auto flex flex-col items-end gap-2 animate-fade-in relative">
                {/* Dismiss × — small, top-right of the stack */}
                <button
                    type="button"
                    onClick={() => setDismissed(true)}
                    data-testid="floating-contact-dismiss"
                    aria-label="Hide contact buttons"
                    className="absolute -top-2 -left-2 w-5 h-5 rounded-full bg-slate-800/80 hover:bg-slate-900 text-white flex items-center justify-center shadow-md transition-all"
                >
                    <X className="w-3 h-3" />
                </button>

                {/* WhatsApp */}
                <a
                    href={waLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={onWaClick}
                    data-testid="floating-contact-whatsapp"
                    className="group flex items-center gap-2 pl-3 pr-4 py-2.5 bg-[#25D366] hover:bg-[#128C7E] text-white rounded-full shadow-lg shadow-emerald-500/30 transition-all font-bold text-sm active:scale-95"
                    aria-label="Chat with Eager on WhatsApp"
                >
                    <MessageCircle className="w-4 h-4 fill-current" />
                    <span>WhatsApp us</span>
                </a>

                {/* Call */}
                <a
                    href={telLink}
                    onClick={onCallClick}
                    data-testid="floating-contact-call"
                    className="group flex items-center gap-2 pl-3 pr-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-full shadow-lg shadow-indigo-500/30 transition-all font-bold text-sm active:scale-95"
                    aria-label="Call Eager directly"
                >
                    <Phone className="w-4 h-4 fill-current" />
                    <span>Call now</span>
                </a>
            </div>

            {/* Availability micro-copy */}
            <div className="pointer-events-none text-[10px] text-slate-500 font-bold uppercase tracking-widest bg-white/80 backdrop-blur px-2 py-0.5 rounded-full shadow-sm">
                {available ? 'Team online' : 'Back within 24h'}
            </div>
        </div>
    );
}
